
import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import { Plus, SquarePen, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllBlogCategories, deleteBlogCategory } from '../../Services/BlogsManagement/BlogsApi';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import DeleteModal from '../../Components/Modals/DeleteModal';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetBlogCategories = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const [blogsData, setBlogsData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Delete modal states
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const fetchBlogsData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllBlogCategories();

            if (response && response.success && response.data) {
                setBlogsData(response.data);
            } else {
                setBlogsData([]);
            }
        } catch (err) {
            console.error(err);
            showSnackbar("Failed to fetch blog categories. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchBlogsData();
    }, [fetchBlogsData]);

    const handleAddClick = () => {
        navigate('/blogs-management/categories/add');
    };

    const handleEditClick = (blog) => {
        navigate('/blogs-management/categories/edit', { state: { blog } });
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteBlogCategory(selectedId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    showSnackbar("Blog category deleted successfully!", "success");
                    fetchBlogsData();
                } else {
                    setDeleteError(response?.message || "Failed to delete blog category");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete blog category";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>



            {/* Header */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 4 }}
            >
                <Typography
                    sx={{
                        color: '#222',
                        fontSize: { xs: '1.75rem', md: '1.8rem' },
                        fontWeight: 500
                    }}
                >
                    Blogs Categories
                </Typography>

                <Button disableFocusRipple disableRipple disableTouchRipple
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={handleAddClick}
                    disableElevation
                    sx={{ bgcolor: '#FFBD65', px: 3, color: '#fff', borderRadius: '4px' }}

                >
                    Add Blog Category
                </Button>
            </Stack>

            {/* Table */}
            <Paper
                sx={{
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    overflow: "hidden"
                }}
                elevation={0}
            >
                <TableContainer>
                    <Table sx={{ minWidth: 1050 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap', textAlign: 'center' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Cover Image</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Updated At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                        <CircularProgress sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : blogsData.length > 0 ? (
                                blogsData.map((blog, idx) => (
                                    <TableRow key={blog.id} sx={{
                                        transition: 'background-color 0.2s',
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}>
                                        <TableCell sx={{ color: '#444', py: 2.5, fontWeight: 500, textAlign: 'center' }}>
                                            {idx + 1}
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Avatar
                                                src={blog.coverImage}
                                                variant="rounded"
                                                sx={{
                                                    width: 60,
                                                    height: 40,
                                                    border: '1px solid #EEE'
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell sx={{ fontWeight: 600, color: '#333', py: 2.5 }}>
                                            {blog.name}
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 300, py: 2.5 }}>
                                            <Typography noWrap title={blog.description} sx={{ fontSize: '0.875rem', color: '#666' }}>
                                                {blog.description || '-'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5, color: '#64748B', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                            {blog.cuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 2.5, color: '#64748B', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                            {blog.uuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 2.5, color: '#64748B', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                            {formatDate(blog.createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ py: 2.5, color: '#64748B', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                            {formatDate(blog.updatedAt)}
                                        </TableCell>

                                        <TableCell align="right" sx={{ py: 2.5 }}>
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#444' }}
                                                        onClick={() => handleEditClick(blog)}
                                                    >
                                                        <SquarePen size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#d32f2f' }}
                                                        onClick={() => handleDeleteClick(blog.id)}
                                                    >
                                                        <Trash2 size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                        No blog categories found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>

                    </Table>
                </TableContainer>
            </Paper>

            <DeleteModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onDelete={handleConfirmDelete}
                title="Delete Blog Category"
                description="Are you sure you want to delete this blog category? This action cannot be undone."
                error={deleteError}
                loading={isDeleting}
            />
        </Box>
    );
};

export default GetBlogCategories;
